"use client";

import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, Loader2, RefreshCcw } from "lucide-react";

import { auditApiV2Get } from "@/lib/auditApiV2";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const TZ = "Asia/Kuala_Lumpur";
const TZ_OFFSET = "+08:00";

type ServicesRow = {
  service: string;
  total: number;
  errors: number;
  error_rate_pct: number | null;
  p95_latency_ms: number | null;
  p99_latency_ms: number | null;
  previous?: any;
  delta?: any;
};

type PathsRow = {
  http_path: string;
  total: number;
  errors: number;
  error_rate_pct: number | null;
  avg_latency_ms: number | null;
  p95_latency_ms: number | null;
  previous?: any;
  delta?: any;
};

type ErrorsRow = {
  error_stack_hash: string | null;
  error_code: string | null;
  total: number;
  first_seen: string;
  last_seen: string;
  example_event_id: number | null;
};

type EventsListResponse = { items: any[]; next_cursor_id: number | null };
type DbChangesListResponse = { items: any[]; next_cursor_id: number | null };

function pad2(n: number) {
  return String(n).padStart(2, "0");
}

function getTzDatePartsNow(tz: string): { year: number; month: number; day: number } {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: tz,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date());
  const year = Number(parts.find((p) => p.type === "year")?.value);
  const month = Number(parts.find((p) => p.type === "month")?.value);
  const day = Number(parts.find((p) => p.type === "day")?.value);
  return { year, month, day };
}

function startOfDayTzIso(tzYear: number, tzMonth: number, tzDay: number) {
  return `${tzYear}-${pad2(tzMonth)}-${pad2(tzDay)}T00:00:00${TZ_OFFSET}`;
}

function endOfDayTzIso(tzYear: number, tzMonth: number, tzDay: number) {
  return `${tzYear}-${pad2(tzMonth)}-${pad2(tzDay)}T23:59:59${TZ_OFFSET}`;
}

function getLast7dRangeIso(): { fromIso: string; toIso: string } {
  const { year, month, day } = getTzDatePartsNow(TZ);
  const todayStart = new Date(startOfDayTzIso(year, month, day));
  const todayEnd = new Date(endOfDayTzIso(year, month, day));
  const from = new Date(todayStart.getTime() - 6 * 24 * 60 * 60 * 1000);
  return { fromIso: from.toISOString(), toIso: todayEnd.toISOString() };
}

function fmtCompact(n: number | null | undefined) {
  const x = typeof n === "number" && Number.isFinite(n) ? n : null;
  if (x === null) return "—";
  return x.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

function fmtPct(n: number | null | undefined) {
  const x = typeof n === "number" && Number.isFinite(n) ? n : null;
  if (x === null) return "—";
  return `${x.toFixed(2)}%`;
}

export function VIPLoggerPage() {
  const [tab, setTab] = useState("services");
  const range = useMemo(() => getLast7dRangeIso(), []);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [services, setServices] = useState<ServicesRow[]>([]);
  const [paths, setPaths] = useState<PathsRow[]>([]);
  const [errors, setErrors] = useState<ErrorsRow[]>([]);

  // Events / DB changes
  const [eventItems, setEventItems] = useState<any[]>([]);
  const [eventCursor, setEventCursor] = useState<number | null>(null);
  const [eventService, setEventService] = useState("");
  const [eventOutcome, setEventOutcome] = useState("");
  const [eventHttpPrefix, setEventHttpPrefix] = useState("");

  const [dbItems, setDbItems] = useState<any[]>([]);
  const [dbCursor, setDbCursor] = useState<number | null>(null);
  const [dbTableName, setDbTableName] = useState("");
  const [dbOperation, setDbOperation] = useState("");

  // Detail dialogs
  const [eventDetailOpen, setEventDetailOpen] = useState(false);
  const [eventDetailLoading, setEventDetailLoading] = useState(false);
  const [eventDetail, setEventDetail] = useState<any | null>(null);
  const [eventDetailError, setEventDetailError] = useState<string | null>(null);

  const [rowHistoryOpen, setRowHistoryOpen] = useState(false);
  const [rowHistoryLoading, setRowHistoryLoading] = useState(false);
  const [rowHistory, setRowHistory] = useState<any[] | null>(null);
  const [rowHistoryError, setRowHistoryError] = useState<string | null>(null);
  const [rowHistoryMeta, setRowHistoryMeta] = useState<{ table_name: string; row_pk: string } | null>(null);

  async function loadServices() {
    const res = await auditApiV2Get<{ items: ServicesRow[] }>("insights/services", {
      from: range.fromIso,
      to: range.toIso,
      tz: TZ,
      limit: 100,
      compare: "previous_period",
    });
    setServices(Array.isArray(res.items) ? res.items : []);
  }

  async function loadPaths() {
    const res = await auditApiV2Get<{ items: PathsRow[] }>("insights/paths", {
      from: range.fromIso,
      to: range.toIso,
      tz: TZ,
      limit: 100,
      compare: "previous_period",
    });
    setPaths(Array.isArray(res.items) ? res.items : []);
  }

  async function loadErrors() {
    const res = await auditApiV2Get<{ items: ErrorsRow[] }>("insights/errors", {
      from: range.fromIso,
      to: range.toIso,
      limit: 50,
    });
    setErrors(Array.isArray(res.items) ? res.items : []);
  }

  async function loadEvents(params?: { reset?: boolean }) {
    const reset = Boolean(params?.reset);
    const cursor = reset ? null : eventCursor;
    const res = await auditApiV2Get<EventsListResponse>("events", {
      from: range.fromIso,
      to: range.toIso,
      limit: 50,
      cursor_id: cursor,
      service: eventService.trim() || null,
      outcome: eventOutcome.trim() || null,
      http_path_prefix: eventHttpPrefix.trim() || null,
    });
    const nextItems = Array.isArray(res.items) ? res.items : [];
    setEventItems((cur) => (reset ? nextItems : [...cur, ...nextItems]));
    setEventCursor(res.next_cursor_id ?? null);
  }

  async function loadDbChanges(params?: { reset?: boolean }) {
    const reset = Boolean(params?.reset);
    const cursor = reset ? null : dbCursor;
    const res = await auditApiV2Get<DbChangesListResponse>("db-changes", {
      from: range.fromIso,
      to: range.toIso,
      limit: 50,
      cursor_id: cursor,
      table_name: dbTableName.trim() || null,
      operation: dbOperation.trim() || null,
    });
    const nextItems = Array.isArray(res.items) ? res.items : [];
    setDbItems((cur) => (reset ? nextItems : [...cur, ...nextItems]));
    setDbCursor(res.next_cursor_id ?? null);
  }

  async function refreshAll() {
    setLoading(true);
    setError(null);
    try {
      await Promise.all([loadServices(), loadPaths(), loadErrors()]);
      setEventItems([]);
      setEventCursor(null);
      setDbItems([]);
      setDbCursor(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load VIP Logger.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refreshAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function openEventDetailById(id: number) {
    setEventDetailOpen(true);
    setEventDetailLoading(true);
    setEventDetail(null);
    setEventDetailError(null);
    try {
      const data = await auditApiV2Get<any>(`events/${id}`);
      setEventDetail(data);
    } catch (e) {
      setEventDetailError(e instanceof Error ? e.message : "Failed to load event.");
    } finally {
      setEventDetailLoading(false);
    }
  }

  async function openRowHistory(table_name: string, row_pk: string) {
    setRowHistoryOpen(true);
    setRowHistoryLoading(true);
    setRowHistory(null);
    setRowHistoryError(null);
    setRowHistoryMeta({ table_name, row_pk });
    try {
      const data = await auditApiV2Get<{ items: any[] }>("rows/history", {
        table_name,
        row_pk,
        limit: 100,
      });
      setRowHistory(Array.isArray(data.items) ? data.items : []);
    } catch (e) {
      setRowHistoryError(e instanceof Error ? e.message : "Failed to load row history.");
    } finally {
      setRowHistoryLoading(false);
    }
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <CardTitle>VIP Logger</CardTitle>
            <CardDescription>Technical audit metrics and drill-down (default: last 7 days).</CardDescription>
          </div>
          <Button variant="outline" className="h-9 font-normal" onClick={refreshAll} disabled={loading}>
            <RefreshCcw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </CardHeader>
      </Card>

      {error && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>VIP Logger failed to load</AlertTitle>
          <AlertDescription className="break-words">{error}</AlertDescription>
        </Alert>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex flex-wrap">
          <TabsTrigger value="services">Services</TabsTrigger>
          <TabsTrigger value="endpoints">Endpoints</TabsTrigger>
          <TabsTrigger value="errors">Errors</TabsTrigger>
          <TabsTrigger value="events">Events</TabsTrigger>
          <TabsTrigger value="db">DB Changes</TabsTrigger>
        </TabsList>

        <TabsContent value="services">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Services</CardTitle>
              <CardDescription>Volume, errors, error rate, and latency percentiles.</CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <Skeleton className="h-[260px] w-full" />
              ) : services.length === 0 ? (
                <div className="text-sm text-muted-foreground">No data.</div>
              ) : (
                <div className="rounded-md border overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Service</TableHead>
                        <TableHead className="text-right">Total</TableHead>
                        <TableHead className="text-right">Errors</TableHead>
                        <TableHead className="text-right">Error %</TableHead>
                        <TableHead className="text-right">p95 (ms)</TableHead>
                        <TableHead className="text-right">p99 (ms)</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {services.slice(0, 100).map((r) => (
                        <TableRow key={r.service}>
                          <TableCell className="text-sm">{r.service}</TableCell>
                          <TableCell className="text-right text-sm font-mono tabular-nums">{fmtCompact(r.total)}</TableCell>
                          <TableCell className="text-right text-sm font-mono tabular-nums">{fmtCompact(r.errors)}</TableCell>
                          <TableCell className="text-right text-sm font-mono tabular-nums">{fmtPct(r.error_rate_pct)}</TableCell>
                          <TableCell className="text-right text-sm font-mono tabular-nums">{fmtCompact(r.p95_latency_ms)}</TableCell>
                          <TableCell className="text-right text-sm font-mono tabular-nums">{fmtCompact(r.p99_latency_ms)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="endpoints">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Endpoints</CardTitle>
              <CardDescription>HTTP paths ranked by volume (with error/latency).</CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <Skeleton className="h-[260px] w-full" />
              ) : paths.length === 0 ? (
                <div className="text-sm text-muted-foreground">No data.</div>
              ) : (
                <div className="rounded-md border overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>HTTP Path</TableHead>
                        <TableHead className="text-right">Total</TableHead>
                        <TableHead className="text-right">Errors</TableHead>
                        <TableHead className="text-right">Error %</TableHead>
                        <TableHead className="text-right">Avg (ms)</TableHead>
                        <TableHead className="text-right">p95 (ms)</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {paths.slice(0, 100).map((r) => (
                        <TableRow key={r.http_path}>
                          <TableCell className="text-sm max-w-[520px] truncate">{r.http_path}</TableCell>
                          <TableCell className="text-right text-sm font-mono tabular-nums">{fmtCompact(r.total)}</TableCell>
                          <TableCell className="text-right text-sm font-mono tabular-nums">{fmtCompact(r.errors)}</TableCell>
                          <TableCell className="text-right text-sm font-mono tabular-nums">{fmtPct(r.error_rate_pct)}</TableCell>
                          <TableCell className="text-right text-sm font-mono tabular-nums">{fmtCompact(r.avg_latency_ms)}</TableCell>
                          <TableCell className="text-right text-sm font-mono tabular-nums">{fmtCompact(r.p95_latency_ms)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="errors">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Error Signatures</CardTitle>
              <CardDescription>Grouped by error stack hash and error code.</CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <Skeleton className="h-[260px] w-full" />
              ) : errors.length === 0 ? (
                <div className="text-sm text-muted-foreground">No errors in range.</div>
              ) : (
                <div className="rounded-md border overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Error Code</TableHead>
                        <TableHead>Stack Hash</TableHead>
                        <TableHead className="text-right">Total</TableHead>
                        <TableHead>Last Seen</TableHead>
                        <TableHead className="text-right">Example</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {errors.map((r, idx) => (
                        <TableRow key={`${r.error_stack_hash}-${r.error_code}-${idx}`}>
                          <TableCell className="text-sm">
                            <Badge variant="secondary" className="font-normal">
                              {r.error_code || "unknown"}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-sm font-mono max-w-[360px] truncate">
                            {r.error_stack_hash || "null"}
                          </TableCell>
                          <TableCell className="text-right text-sm font-mono tabular-nums">{fmtCompact(r.total)}</TableCell>
                          <TableCell className="text-sm text-muted-foreground">{String(r.last_seen).slice(0, 19)}</TableCell>
                          <TableCell className="text-right">
                            <Button
                              variant="outline"
                              className="h-8 font-normal"
                              disabled={!r.example_event_id}
                              onClick={() => r.example_event_id && void openEventDetailById(r.example_event_id)}
                            >
                              View
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="events">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Events</CardTitle>
              <CardDescription>Raw audit events (cursor pagination).</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex flex-col md:flex-row md:items-center gap-2">
                <Input
                  placeholder="Filter service (exact)"
                  value={eventService}
                  onChange={(e) => setEventService(e.currentTarget.value)}
                  className="h-9 md:max-w-[220px]"
                />
                <Input
                  placeholder="Filter outcome (e.g. error)"
                  value={eventOutcome}
                  onChange={(e) => setEventOutcome(e.currentTarget.value)}
                  className="h-9 md:max-w-[220px]"
                />
                <Input
                  placeholder="HTTP path prefix (e.g. /functions/v1/)"
                  value={eventHttpPrefix}
                  onChange={(e) => setEventHttpPrefix(e.currentTarget.value)}
                  className="h-9 flex-1"
                />
                <Button
                  variant="outline"
                  className="h-9 font-normal"
                  onClick={() => void loadEvents({ reset: true })}
                  disabled={loading}
                >
                  {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Apply
                </Button>
              </div>

              <div className="rounded-md border overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Time</TableHead>
                      <TableHead>Service</TableHead>
                      <TableHead>Path</TableHead>
                      <TableHead>Outcome</TableHead>
                      <TableHead className="text-right">Status</TableHead>
                      <TableHead className="text-right">Latency</TableHead>
                      <TableHead className="text-right">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {eventItems.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} className="py-10 text-center text-muted-foreground">
                          No events loaded yet.
                        </TableCell>
                      </TableRow>
                    ) : (
                      eventItems.map((r) => (
                        <TableRow key={String(r.id)}>
                          <TableCell className="text-sm text-muted-foreground">{String(r.created_at).slice(0, 19)}</TableCell>
                          <TableCell className="text-sm">{r.service}</TableCell>
                          <TableCell className="text-sm max-w-[320px] truncate">{r.http_path}</TableCell>
                          <TableCell className="text-sm">
                            <Badge variant={r.outcome === "error" ? "destructive" : "secondary"} className="font-normal">
                              {r.outcome}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right text-sm font-mono tabular-nums">{fmtCompact(r.response_status)}</TableCell>
                          <TableCell className="text-right text-sm font-mono tabular-nums">{fmtCompact(r.latency_ms)}</TableCell>
                          <TableCell className="text-right">
                            <Button variant="outline" className="h-8 font-normal" onClick={() => void openEventDetailById(Number(r.id))}>
                              View
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>

              <div className="flex items-center justify-between">
                <div className="text-xs text-muted-foreground">
                  Range: {range.fromIso.slice(0, 10)} → {range.toIso.slice(0, 10)} ({TZ})
                </div>
                <Button
                  variant="outline"
                  className="h-9 font-normal"
                  onClick={() => void loadEvents({ reset: false })}
                  disabled={eventCursor === null}
                >
                  Load more
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="db">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">DB Changes</CardTitle>
              <CardDescription>Audit trail of table operations.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex flex-col md:flex-row md:items-center gap-2">
                <Input
                  placeholder="Filter table_name (exact)"
                  value={dbTableName}
                  onChange={(e) => setDbTableName(e.currentTarget.value)}
                  className="h-9 md:max-w-[260px]"
                />
                <Input
                  placeholder="Filter operation (INSERT/UPDATE/DELETE)"
                  value={dbOperation}
                  onChange={(e) => setDbOperation(e.currentTarget.value)}
                  className="h-9 md:max-w-[260px]"
                />
                <Button
                  variant="outline"
                  className="h-9 font-normal"
                  onClick={() => void loadDbChanges({ reset: true })}
                  disabled={loading}
                >
                  {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Apply
                </Button>
              </div>

              <div className="rounded-md border overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Time</TableHead>
                      <TableHead>Table</TableHead>
                      <TableHead>Operation</TableHead>
                      <TableHead>Row PK</TableHead>
                      <TableHead className="text-right">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {dbItems.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="py-10 text-center text-muted-foreground">
                          No DB changes loaded yet.
                        </TableCell>
                      </TableRow>
                    ) : (
                      dbItems.map((r) => (
                        <TableRow key={String(r.id)}>
                          <TableCell className="text-sm text-muted-foreground">{String(r.created_at).slice(0, 19)}</TableCell>
                          <TableCell className="text-sm">{r.table_name}</TableCell>
                          <TableCell className="text-sm">
                            <Badge variant="outline" className="font-normal">
                              {r.operation}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-sm font-mono max-w-[360px] truncate">{r.row_pk}</TableCell>
                          <TableCell className="text-right">
                            <Button
                              variant="outline"
                              className="h-8 font-normal"
                              onClick={() => void openRowHistory(String(r.table_name), String(r.row_pk))}
                            >
                              History
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>

              <div className="flex items-center justify-between">
                <div className="text-xs text-muted-foreground">
                  Range: {range.fromIso.slice(0, 10)} → {range.toIso.slice(0, 10)} ({TZ})
                </div>
                <Button
                  variant="outline"
                  className="h-9 font-normal"
                  onClick={() => void loadDbChanges({ reset: false })}
                  disabled={dbCursor === null}
                >
                  Load more
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={eventDetailOpen} onOpenChange={setEventDetailOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Event detail</DialogTitle>
            <DialogDescription>Raw `audit_event` row.</DialogDescription>
          </DialogHeader>
          {eventDetailLoading ? (
            <Skeleton className="h-[320px] w-full" />
          ) : eventDetailError ? (
            <div className="text-sm text-destructive break-words">{eventDetailError}</div>
          ) : (
            <pre className="max-h-[60vh] overflow-auto rounded-md border bg-muted/20 p-3 text-xs">
              {JSON.stringify(eventDetail, null, 2)}
            </pre>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={rowHistoryOpen} onOpenChange={setRowHistoryOpen}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>Row history</DialogTitle>
            <DialogDescription>
              {rowHistoryMeta ? (
                <span className="font-mono text-xs">
                  {rowHistoryMeta.table_name} / {rowHistoryMeta.row_pk}
                </span>
              ) : (
                "Audit trail for a specific row."
              )}
            </DialogDescription>
          </DialogHeader>
          {rowHistoryLoading ? (
            <Skeleton className="h-[320px] w-full" />
          ) : rowHistoryError ? (
            <div className="text-sm text-destructive break-words">{rowHistoryError}</div>
          ) : !rowHistory || rowHistory.length === 0 ? (
            <div className="text-sm text-muted-foreground">No history found.</div>
          ) : (
            <div className="rounded-md border overflow-hidden max-h-[60vh] overflow-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Time</TableHead>
                    <TableHead>Op</TableHead>
                    <TableHead>Reason</TableHead>
                    <TableHead className="text-right">ID</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rowHistory.map((r) => (
                    <TableRow key={String(r.id)}>
                      <TableCell className="text-sm text-muted-foreground">{String(r.created_at).slice(0, 19)}</TableCell>
                      <TableCell className="text-sm">
                        <Badge variant="outline" className="font-normal">
                          {r.operation}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground max-w-[520px] truncate">
                        {String(r.change_reason ?? "")}
                      </TableCell>
                      <TableCell className="text-right text-sm font-mono tabular-nums">{fmtCompact(Number(r.id))}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

